if (!customElements.get('speakon-agent-definition')) {
  customElements.define('speakon-agent-definition', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      const stage = this.querySelector('.speakon-agent-definition__stage');
      const content = this.querySelector('.speakon-agent-definition__content');
      const product = this.querySelector('.speakon-agent-definition__product');
      const exit = this.querySelector('.speakon-agent-definition__exit-wash');
      const sentences = [...this.querySelectorAll('.speakon-agent-definition__sentence')];
      if (!stage || !content || !sentences.length || this.dataset.scrollReveal !== 'true') return;

      const clamp = value => Math.max(0, Math.min(1, value));
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = matchMedia('(max-width: 767px)');
      const groups = sentences.map(sentence => ({
        sentence,
        marks: [...sentence.querySelectorAll('.speakon-agent-definition__highlight')],
        fragments: [],
        width: 0,
        progress: 0,
        target: 0,
        from: 0,
        startedAt: 0
      }));
      this.events = new AbortController();
      const { signal } = this.events;
      let needsMeasure = true;
      let distance = 1;
      let stickyTop = 0;
      let enteredOnMobile = false;
      const render = now => {
        this.frame = null;
        if (!this.hasAttribute('data-reveal-active')) return;
        if (needsMeasure) {
          stickyTop = Number.parseFloat(getComputedStyle(stage).top) || 0;
          distance = Math.max(1, this.offsetHeight - stage.offsetHeight);
          groups.forEach(group => {
            let offset = 0;
            group.fragments = group.marks.map(mark => {
              const width = Math.max(1, mark.getBoundingClientRect().width);
              const fragment = { mark, offset, width };
              offset += width;
              return fragment;
            });
            group.width = offset;
          });
          needsMeasure = false;
        }
        let progress;
        if (mobile.matches) {
          const bounds = content.getBoundingClientRect();
          enteredOnMobile ||= bounds.top < window.innerHeight && bounds.bottom > 0;
          progress = Number(enteredOnMobile);
        } else {
          progress = clamp((stickyTop - this.getBoundingClientRect().top) / distance);
        }
        const drift = progress * progress * (3 - 2 * progress);
        const reading = clamp((progress - .15) / .69);
        product?.style.setProperty('--agent-definition-progress', progress.toFixed(4));
        product?.style.setProperty('--agent-definition-drift', drift.toFixed(4));
        const exitProgress = clamp((progress - .88) / .12);
        exit?.style.setProperty('--agent-definition-exit', (exitProgress * exitProgress * (3 - 2 * exitProgress) * .45).toFixed(4));
        let animating = false;
        groups.forEach((group, index) => {
          const threshold = .12 + (index - 1) * .62 / Math.max(1, groups.length - 2);
          const target = Number(index === 0 ? progress >= .1 : reading >= threshold);
          group.sentence.classList.toggle('is-revealed', Boolean(target));
          if (target !== group.target) {
            group.from = group.progress;
            group.target = target;
            group.startedAt = now;
          }
          // Mobile reveals every sentence on entry; each 650ms wipe finishes without scrolling.
          const elapsed = clamp((now - group.startedAt) / 650);
          group.progress = group.from + (group.target - group.from) * (1 - (1 - elapsed) ** 3);
          const edge = group.progress * group.width;
          group.fragments.forEach(({ mark, offset, width }) => {
            mark.style.setProperty('--agent-definition-fill', clamp((edge - offset) / width).toFixed(4));
          });
          animating ||= group.marks.length > 0 && group.progress !== group.target;
        });
        if (animating) schedule();
      };
      const schedule = () => {
        if (!this.frame && this.hasAttribute('data-reveal-active')) this.frame = requestAnimationFrame(render);
      };
      const remeasure = () => {
        needsMeasure = true;
        schedule();
      };
      const syncMotion = () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.toggleAttribute('data-reveal-active', !reducedMotion.matches);
        if (reducedMotion.matches) {
          groups.forEach(group => group.marks.forEach(mark => mark.style.removeProperty('--agent-definition-fill')));
        }
        remeasure();
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', remeasure, { passive: true, signal });
      reducedMotion.addEventListener('change', syncMotion, { signal });
      mobile.addEventListener('change', syncMotion, { signal });
      this.resizeObserver = new ResizeObserver(remeasure);
      this.resizeObserver.observe(stage);
      this.resizeObserver.observe(content);
      document.fonts.ready.then(() => {
        if (!signal.aborted) remeasure();
      });
      syncMotion();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.removeAttribute('data-reveal-active');
      this.querySelectorAll('.speakon-agent-definition__highlight').forEach(mark => mark.style.removeProperty('--agent-definition-fill'));
    }
  });
}
